import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Rating,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Badge,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ButtonGroup,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AlertTitle,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Feedback as FeedbackIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  QuestionAnswer as QuestionAnswerIcon,
  History as HistoryIcon,
  Notes as NotesIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Pause as PauseIcon,
  Recommend as RecommendIcon,
  Notifications as NotificationsIcon,
  ExpandMore as ExpandMoreIcon,
  ViewModule as ViewModuleIcon,
  GetApp as GetAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { resumeService } from '../../services/resumeService';
import { feedbackService } from '../../services/feedbackService';
import dayjs from 'dayjs';
import COLORS from '../../config/colors';

interface Interview {
  id: string;
  candidateName: string;
  candidateEmail: string;
  phone?: string;
  role: string;
  jobId: string;
  fitmentScore: number;
  status: string;
  scheduledTime?: string;
  completedTime?: string;
  techStack: string[];
  resume?: any;
  location?: string;
  experience?: number;
  education?: string;
  originalStatus?: string;
  panelNotes?: string;
  feedbackSubmitted?: boolean;
  outcome?: 'Pass' | 'Fail' | 'Hold' | 'Recommend' | null;
  interviewHistory?: any[];
  suggestedQuestions?: string[];
  scorecardSummary?: any;
}

interface PanelistInterviewsViewProps {
  panelistEmail: string | null;
  panelistName: string;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const PanelistInterviewsView: React.FC<PanelistInterviewsViewProps> = ({
  panelistEmail,
  panelistName,
  onError,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [techStackFilter, setTechStackFilter] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState<Interview | null>(null);
  const [candidateDetailsModal, setCandidateDetailsModal] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackModalData, setFeedbackModalData] = useState<any>(null);
  const [panelNotes, setPanelNotes] = useState('');
  const [selectedOutcome, setSelectedOutcome] = useState<Interview['outcome']>(null);

  // Tab definitions
  const tabs = [
    { label: 'My Interviews', value: 0, icon: <AssignmentIcon /> },
    { label: 'Completed', value: 1, icon: <CheckCircleIcon /> },
    { label: 'Feedback Pending', value: 2, icon: <PendingActionsIcon /> },
    { label: 'Schedule', value: 3, icon: <ScheduleIcon /> },
  ];

  const loadInterviews = async () => {
    if (!panelistEmail) {
      onError('Email address is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Loading interviews for panelist email:', panelistEmail);
      
      const response = await resumeService.getResumesByPanelistEmail(panelistEmail, 1, 100);
      console.log('Panelist API Response:', response);
      
      const interviewList: Interview[] = response.items.map((resume: any) => ({
        id: resume.resume_id,
        candidateName: resume.candidate_name,
        candidateEmail: resume.candidate_email,
        phone: resume.phone || '',
        role: resume.job_title || `Job ${resume.job_id}`,
        jobId: resume.job_id,
        fitmentScore: resume.score || 0,
        status: 'scheduled',
        scheduledTime: resume.scheduled_time || resume.interview_time || undefined,
        completedTime: resume.completed_time || resume.interview_completed_time || undefined,
        techStack: resume.skills || [],
        resume: resume,
        location: resume.location || '',
        experience: resume.experience_years || 0,
        education: resume.education || '',
        panelNotes: resume.notes || '',
        feedbackSubmitted: resume.feedback_submitted || false,
        originalStatus: resume.resume_status || '',
        outcome: resume.outcome || null,
        interviewHistory: resume.interview_history || [],
        suggestedQuestions: resume.suggested_questions || [],
        scorecardSummary: resume.scorecard_summary || null,
      }));
      
      setInterviews(interviewList);
      console.log(`Loaded ${interviewList.length} interviews for panelist: ${panelistEmail}`);
      
    } catch (error) {
      console.error('Error loading interviews:', error);
      onError(`Failed to load interviews: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterviews();
  }, [panelistEmail]);

  // Filter interviews based on search and filters
  const getFilteredInterviews = () => {
    let filtered = interviews;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(interview =>
        interview.candidateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        interview.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
        interview.candidateEmail.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(interview => interview.originalStatus === statusFilter);
    }

    // Tech stack filter
    if (techStackFilter) {
      filtered = filtered.filter(interview =>
        interview.techStack.some(skill =>
          skill.toLowerCase().includes(techStackFilter.toLowerCase())
        )
      );
    }

    // Tab-specific filtering
    switch (activeTab) {
      case 0: // My Interviews - All interviews
        return filtered;
      case 1: // Completed
        return filtered.filter(interview =>
          interview.originalStatus && ['completed', 'interviewed', 'done'].includes(interview.originalStatus.toLowerCase())
        );
      case 2: // Feedback Pending
        return filtered.filter(interview =>
          !interview.feedbackSubmitted && 
          interview.originalStatus && !['scheduled', 'pending'].includes(interview.originalStatus.toLowerCase())
        );
      case 3: // Schedule
        return filtered.filter(interview =>
          interview.originalStatus && ['scheduled', 'pending'].includes(interview.originalStatus.toLowerCase())
        );
      default:
        return filtered;
    }
  };

  // Check for upcoming interviews (within 2 hours)
  const getUpcomingInterviews = () => {
    const now = dayjs();
    const twoHoursLater = now.add(2, 'hours');
    
    return interviews.filter(interview => {
      if (!interview.scheduledTime) return false;
      const scheduledTime = dayjs(interview.scheduledTime);
      return scheduledTime.isAfter(now) && scheduledTime.isBefore(twoHoursLater);
    });
  };

  const handleCandidateClick = (interview: Interview) => {
    setSelectedCandidate(interview);
    setPanelNotes(interview.panelNotes || '');
    setSelectedOutcome(interview.outcome || null);
    setCandidateDetailsModal(true);
  };

  const handleViewQuestions = () => {
    if (!selectedCandidate) return;
    
    // Navigate to Agent screen with candidate data
    navigate(`/agent/${selectedCandidate.id}`, {
      state: {
        candidate: {
          id: selectedCandidate.id,
          name: selectedCandidate.candidateName,
          email: selectedCandidate.candidateEmail,
          role: selectedCandidate.role,
          jobId: selectedCandidate.jobId,
          fitmentScore: selectedCandidate.fitmentScore,
          techStack: selectedCandidate.techStack,
          experience: selectedCandidate.experience,
          location: selectedCandidate.location,
          phone: selectedCandidate.phone,
        },
        context: 'interview-questions',
        panelist: panelistName,
        panelistEmail: panelistEmail,
      }
    });
  };

  const handleSubmitFeedback = () => {
    if (!selectedCandidate) return;
    
    setFeedbackModalData({
      id: selectedCandidate.id,
      resume_id: selectedCandidate.id,
      job_id: selectedCandidate.jobId,
      candidate_name: selectedCandidate.candidateName,
      candidate_email: selectedCandidate.candidateEmail,
      skills: selectedCandidate.techStack || [],
      location: selectedCandidate.location || '',
      experience_years: selectedCandidate.experience || 0,
      resume_status: selectedCandidate.originalStatus || '',
      notes: panelNotes,
      phone: selectedCandidate.phone || '',
      education: selectedCandidate.education || '',
      panel_list: selectedCandidate.resume?.panel_list || [],
      createdAt: selectedCandidate.resume?.createdAt || '',
      updatedAt: selectedCandidate.resume?.updatedAt || '',
      score: selectedCandidate.fitmentScore || 0,
      panelist: panelistName,
      panelistEmail: panelistEmail,
      outcome: selectedOutcome,
    });
    
    setFeedbackModalOpen(true);
  };

  const handleFeedbackSubmit = async (feedbackData: any) => {
    try {
      console.log('Submitting panel feedback:', feedbackData);
      
      const resumeId = selectedCandidate?.id;
      
      if (!resumeId) {
        throw new Error('Resume ID is required for feedback submission');
      }
      
      await feedbackService.submitPanelistFeedback(resumeId, feedbackData);
      
      setInterviews(prev => prev.map(interview => 
        interview.id === selectedCandidate?.id 
          ? { 
              ...interview, 
              feedbackSubmitted: true,
              panelNotes: feedbackData.notes || interview.panelNotes,
              outcome: feedbackData.outcome || interview.outcome,
            }
          : interview
      ));
      
      onSuccess('Feedback submitted successfully!');
      
      setFeedbackModalOpen(false);
      setFeedbackModalData(null);
      setCandidateDetailsModal(false);
      
      await loadInterviews();
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      onError(`Failed to submit feedback: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleOutcomeChange = (outcome: Interview['outcome']) => {
    setSelectedOutcome(outcome);
    if (selectedCandidate) {
      setInterviews(prev => prev.map(interview => 
        interview.id === selectedCandidate.id 
          ? { ...interview, outcome }
          : interview
      ));
    }
  };

  const handleNotesChange = (notes: string) => {
    setPanelNotes(notes);
    if (selectedCandidate) {
      setInterviews(prev => prev.map(interview => 
        interview.id === selectedCandidate.id 
          ? { ...interview, panelNotes: notes }
          : interview
      ));
    }
  };

  const filteredInterviews = getFilteredInterviews();
  const upcomingInterviews = getUpcomingInterviews();

  const stats = {
    total: interviews.length,
    completed: interviews.filter(i => 
      i.originalStatus && ['completed', 'interviewed', 'done'].includes(i.originalStatus.toLowerCase())
    ).length,
    feedbackPending: interviews.filter(i => 
      !i.feedbackSubmitted && 
      i.originalStatus && !['scheduled', 'pending'].includes(i.originalStatus.toLowerCase())
    ).length,
    scheduled: interviews.filter(i => 
      i.originalStatus && ['scheduled', 'pending'].includes(i.originalStatus.toLowerCase())
    ).length,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading interviews...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Upcoming Interviews Notification */}
      {upcomingInterviews.length > 0 && (
        <Alert 
          severity="warning" 
          icon={<NotificationsIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upcoming Interviews - Next 2 Hours
          </Typography>
          {upcomingInterviews.map(interview => (
            <Typography key={interview.id} variant="body2">
              • {interview.candidateName} - {interview.role} at {dayjs(interview.scheduledTime).format('HH:mm')}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Interviews
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                {stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Badge badgeContent={stats.feedbackPending} color="error">
                <Typography variant="h4" color="warning.main" sx={{ fontWeight: 700 }}>
                  {stats.feedbackPending}
                </Typography>
              </Badge>
              <Typography variant="body2" color="text.secondary">
                Feedback Pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 700 }}>
                {stats.scheduled}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scheduled
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search candidates by name, role, or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                  startAdornment={<FilterListIcon sx={{ mr: 1 }} />}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="interviewed">Interviewed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by tech stack"
                value={techStackFilter}
                onChange={(e) => setTechStackFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                  setTechStackFilter('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map(tab => (
            <Tab 
              key={tab.value}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon}
                  {tab.label}
                  {tab.value === 2 && stats.feedbackPending > 0 && (
                    <Badge badgeContent={stats.feedbackPending} color="error" />
                  )}
                </Box>
              }
              sx={{ textTransform: 'none', fontWeight: 600 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Interviews Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {tabs[activeTab].label} ({filteredInterviews.length})
            </Typography>
          </Box>
          
          {filteredInterviews.length === 0 ? (
            <Alert severity="info">
              No interviews found for the selected criteria.
            </Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Fitment Score</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scheduled Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInterviews.map(interview => (
                  <TableRow key={interview.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: COLORS.PRIMARY }}>
                          {interview.candidateName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ fontWeight: 600, cursor: 'pointer', color: COLORS.PRIMARY }}
                            onClick={() => handleCandidateClick(interview)}
                          >
                            {interview.candidateName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {interview.candidateEmail}
                          </Typography>
                          {interview.techStack.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {interview.techStack.slice(0, 3).map((skill, index) => (
                                <Chip 
                                  key={index} 
                                  label={skill} 
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              ))}
                              {interview.techStack.length > 3 && (
                                <Chip 
                                  label={`+${interview.techStack.length - 3}`} 
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {interview.role}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {interview.jobId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={interview.fitmentScore / 20} readOnly size="small" />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {interview.fitmentScore}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          label={interview.originalStatus || 'No Status'}
                          color={interview.originalStatus ? 'primary' : 'default'}
                          size="small"
                        />
                        {interview.outcome && (
                          <Chip
                            label={interview.outcome}
                            color={
                              interview.outcome === 'Pass' ? 'success' :
                              interview.outcome === 'Fail' ? 'error' :
                              interview.outcome === 'Hold' ? 'warning' :
                              'info'
                            }
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {interview.scheduledTime 
                          ? dayjs(interview.scheduledTime).format('MMM DD, YYYY HH:mm')
                          : 'Not scheduled'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleCandidateClick(interview)}
                        >
                          View Details
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<FeedbackIcon />}
                          onClick={() => {
                            setSelectedCandidate(interview);
                            handleSubmitFeedback();
                          }}
                          disabled={interview.feedbackSubmitted}
                          color={interview.feedbackSubmitted ? 'success' : 'primary'}
                        >
                          {interview.feedbackSubmitted ? 'Submitted' : 'Feedback'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Candidate Details Modal -   */}
      <Dialog
        open={candidateDetailsModal}
        onClose={() => setCandidateDetailsModal(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { 
            height: '90vh',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 1,
        }}>
          <Typography variant="h6">
            {selectedCandidate?.candidateName} - Interview Details
          </Typography>
          <IconButton onClick={() => setCandidateDetailsModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', p: 3 }}>
          {selectedCandidate && (
            <Box>
              {/* Candidate Basic Info */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  Candidate Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1">{selectedCandidate.candidateEmail}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Phone</Typography>
                    <Typography variant="body1">{selectedCandidate.phone || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Experience</Typography>
                    <Typography variant="body1">{selectedCandidate.experience} years</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Location</Typography>
                    <Typography variant="body1">{selectedCandidate.location}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Skills</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {selectedCandidate.techStack.map((skill, index) => (
                        <Chip 
                          key={index} 
                          label={skill} 
                          size="small"
                          sx={{ backgroundColor: '#f0f9ff', color: COLORS.PRIMARY }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Grid layout for sections -   */}
              <Grid container spacing={3}>
                {/* Resume Access */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, minHeight: '200px' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DownloadIcon />
                      Resume Access
                    </Typography>
                    <Stack spacing={1} direction="row">
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        size="small"
                      >
                        View Resume
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        size="small"
                      >
                        Download
                      </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Last updated: {selectedCandidate.resume?.updatedAt 
                        ? dayjs(selectedCandidate.resume.updatedAt).format('MMM DD, YYYY')
                        : 'N/A'
                      }
                    </Typography>
                  </Paper>
                </Grid>

                {/* Scorecard Summary */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, minHeight: '200px' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon />
                      Scorecard Summary
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="body2">Overall Score:</Typography>
                      <Rating value={selectedCandidate.fitmentScore / 20} readOnly />
                      <Typography variant="h6" color="primary">
                        {selectedCandidate.fitmentScore}%
                      </Typography>
                    </Box>
                    {selectedCandidate.scorecardSummary ? (
                      <Typography variant="body2" color="text.secondary">
                        {selectedCandidate.scorecardSummary}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No scorecard summary available yet.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Suggested Questions -   */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, minHeight: '250px', overflow: 'visible' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QuestionAnswerIcon />
                      Suggested Questions
                    </Typography>
                    
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        AI-generated questions based on candidate profile and job requirements?.
                      </Typography>
                      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Button
                          variant="outlined"
                          startIcon={<ViewModuleIcon />}
                          size="small"
                          onClick={handleViewQuestions}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                          }}
                        >
                          View Questions
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<GetAppIcon />}
                          size="small"
                          sx={{ borderRadius: 2 }}
                        >
                          Download
                        </Button>
                      </Stack>
                      
                      {/* Sample Questions in a simple box instead of accordion */}
                      <Box sx={{ 
                        p: 2, 
                        bgcolor:  COLORS.GRAY_100 , 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Sample Questions:
                        </Typography>
                        <List dense>
                          <ListItem sx={{ py: 0.5 }}>
                            <Typography variant="body2">
                              • Tell me about your experience with {selectedCandidate?.techStack[0] || 'relevant technologies'}
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <Typography variant="body2">
                              • How would you handle a challenging project deadline?
                            </Typography>
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <Typography variant="body2">
                              • Describe your approach to problem-solving
                            </Typography>
                          </ListItem>
                        </List>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Interview History */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, minHeight: '250px', overflow: 'auto' }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon />
                      Interview History
                    </Typography>
                    {selectedCandidate.interviewHistory && selectedCandidate.interviewHistory.length > 0 ? (
                      <List dense>
                        {selectedCandidate.interviewHistory.map((history, index) => (
                          <ListItem key={index} divider>
                            <ListItemAvatar>
                              <Avatar sx={{ width: 24, height: 24, fontSize: '0.8rem' }}>
                                {history.panelist?.charAt(0) || 'P'}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={history.panelist || 'Unknown Panelist'}
                              secondary={
                                <Box>
                                  <Typography variant="caption">
                                    {history.feedback || 'No feedback'}
                                  </Typography>
                                  <Chip
                                    label={history.status || 'No status'}
                                    size="small"
                                    sx={{ ml: 1 }}
                                  />
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No previous interview history.
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Panel Notes */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotesIcon />
                      Panel Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={panelNotes}
                      onChange={(e) => handleNotesChange(e.target.value)}
                      placeholder="Enter your notes about the candidate during the interview..."
                      variant="outlined"
                    />
                  </Paper>
                </Grid>

                {/* Outcome Buttons */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Interview Outcome</Typography>
                    <ButtonGroup variant="outlined" sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        startIcon={<ThumbUpIcon />}
                        onClick={() => handleOutcomeChange('Pass')}
                        variant={selectedOutcome === 'Pass' ? 'contained' : 'outlined'}
                        color="success"
                      >
                        Pass
                      </Button>
                      <Button
                        startIcon={<ThumbDownIcon />}
                        onClick={() => handleOutcomeChange('Fail')}
                        variant={selectedOutcome === 'Fail' ? 'contained' : 'outlined'}
                        color="error"
                      >
                        Fail
                      </Button>
                      <Button
                        startIcon={<PauseIcon />}
                        onClick={() => handleOutcomeChange('Hold')}
                        variant={selectedOutcome === 'Hold' ? 'contained' : 'outlined'}
                        color="warning"
                      >
                        Hold
                      </Button>
                      <Button
                        startIcon={<RecommendIcon />}
                        onClick={() => handleOutcomeChange('Recommend')}
                        variant={selectedOutcome === 'Recommend' ? 'contained' : 'outlined'}
                        color="info"
                      >
                        Recommend
                      </Button>
                    </ButtonGroup>
                    {selectedOutcome && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Outcome selected: <strong>{selectedOutcome}</strong>
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PanelistInterviewsView;